<template>
    <div :class="switchClass">
        <common-loader :isProcessing="isProcessing"></common-loader>

        <ul class="member-switch__items">
            <li v-for="member in availableMembers" :key="member.id" :class="itemClass(member)" @click="switchTo(member)">
                <div class="member-switch__avatar">
                    <common-user-avatar :user="member" :isCurrent="isCurrent(member)" size="member-switch"></common-user-avatar>
                </div>

                <div>
                    <h5 class="member-switch__name">
                        <common-user-name :user="member"></common-user-name>
                    </h5>

                    <p class="member-switch__role">
                        {{ member.userRole.name }}
                    </p>
                </div>

                <template v-if="member.isSignupFinished">
                    <div v-if="member.newReferralsCount" class="member-switch__notifications">
                        <common-notifications-badge :count="member.newReferralsCount"></common-notifications-badge>
                    </div>

                    <div class="member-switch__switch-to">
                        <common-button :label="switchToLabel" type="primary" size="tiny"></common-button>
                    </div>
                </template>
                <template v-else>
                    <div class="member-switch__warning-icon">
                        <common-icon name="warning" size="member-warning"></common-icon>
                    </div>

                    <div class="member-switch__warning-message">
                        <span>{{ $t('common.users_list.unavailable') }}</span>
                        <br />
                        <div class="member-progress">
                            <span>
                                {{ $t('elements.referrals.card.email_verification_pending') }}
                            </span>
                        </div>
                        <a v-if="!emailValidationReminderSent" class="reminder-link" @click="sendReminder($event, member)">
                            {{ $t('elements.referrals.card.send_reminder') }}
                        </a>
                        <div v-if="emailValidationReminderSent" class="success-notification">
                            <common-icon name="success" size="small"></common-icon>
                            <p>{{ $t('elements.referrals.card.reminder_sent') }}</p>
                        </div>
                    </div>
                </template>
            </li>
        </ul>
    </div>
</template>

<script>
    import UsersAPI from 'api/UsersAPI'
    import UserRolesEnum from '@/enums/UserRolesEnum'

    export default {
        props: {
            members: { type: Array, default: undefined },
            switchToLabel: { type: String, required: true },
            isNewReferralsDisplayed: { type: Boolean, default: false },
            isProcessing: { type: Boolean, default: false },
            isStandalone: { type: Boolean, default: false },
        },

        data() {
            return {
                emailValidationReminderSent: false,
            }
        },

        computed: {
            switchClass() {
                return {
                    'member-switch': true,
                    'member-switch--is-standalone': this.isStandalone,
                }
            },

            availableMembers() {
                return this.members.filter(
                    ({ userRole }) =>
                        userRole && [UserRolesEnum.PHYSICIAN.ID, UserRolesEnum.NURSE_PRACTITIONER.ID].includes(userRole.id),
                )
            },
        },

        methods: {
            isCurrent({ id }) {
                return this.$auth.getActiveMember() ? parseInt(this.$auth.getActiveMember().id) === parseInt(id) : false
            },

            itemClass(member) {
                const isHighlighted = this.$auth.getActiveMember().id === this.$auth.getUser().id || this.isCurrent(member)
                const isUnavailable = !member.isSignupFinished

                return {
                    'member-switch__item': true,
                    'member-switch__item--is-highlighted': isHighlighted && !isUnavailable,
                    'member-switch__item--is-unavailable': isUnavailable,
                }
            },

            indicateScrollability() {
                this.$nextTick(() => {
                    const initialScrollTop = this.$el.scrollTop
                    this.$el.scrollTo(0, initialScrollTop + 1)
                    this.$el.scrollTo(0, initialScrollTop)
                })
            },

            switchTo(member) {
                if (member.isSignupFinished) {
                    this.$auth.setActiveMember(member)
                    this.$auth.setActiveUserLocations(member.id)
                    this.$route.name === 'index.home' ? this.$router.go() : this.$router.push({ name: 'index.home' })
                }
            },

            getMemberName(member) {
                return (
                    member.name || `${member.firstName} ${member.lastName} ${member.designation || member.title || ''}`
                ).trim()
            },

            sendReminder(event, member) {
                if (event && event.preventDefault) {
                    event.preventDefault()
                }
                return UsersAPI.sendVerificationReminderEmail(this.$auth.getActiveMember().id, member.id).then(() => {
                    this.emailValidationReminderSent = true
                })
            },
        },

        watch: {
            members() {
                this.indicateScrollability()
            },
        },

        mounted() {
            this.indicateScrollability()
        },
    }
</script>

<style scoped>
    .reminder-link {
        color: #13b3e3;
    }

    .member-progress {
        margin-top: 1em;
        color: #999;
    }
</style>
